// index.ts — entry point for the catalog-list bundle (IIFE → includes/js/dist/catalog-list-bundle.js).
//
// The "JSON API + TS list-renderer" pattern, piloted on ancestries.php (see
// plans/can-we-update-ancestries-php-snazzy-cook.md). A page drops a
// <div data-ft-catalog-list data-ft-endpoint="…"> mount; this bundle fetches the JSON catalog
// from that endpoint and paints the cards/table client-side, wiring live search + the filter panel.
// Vanilla DOM + fetch only — no jQuery. The heavy item compile + filter logic stays in PHP.
//
// Also ships ft-sortable: in-place sort/filter for existing server-rendered INTERACTIVE tables
// (class="ft-sortable") that can't be JSON snapshots — the jQuery-free tablesorter replacement there.
//
// Exposes window.ftMountCatalogLists() (re-hydrate AJAX-injected catalog mounts) and
// window.ftSortableScan() (re-enhance AJAX-injected .ft-sortable tables). Both guard against
// double-mounting, so re-scans are no-ops on already-mounted elements.

import '../object-card/item-layer-audit';
import { mountCatalogLists } from './catalog-list';
import { scanSortable } from './ft-sortable';
import { scanCardFilters } from './card-filter';
import { requestCatalogRowRemoval } from './render-table';
import { renderCampaignPlayersTool } from './render-my-campaign-players';

declare global {
    interface Window {
        ftMountCatalogLists: typeof mountCatalogLists;
        ftSortableScan: typeof scanSortable;
        // Remove a catalog-table row by item_id WITHOUT a full page reload — fired by a per-row delete
        // button (my_encounters etc.) after the server soft-delete succeeds. Dispatches a CustomEvent
        // the owning render-table mount listens for; a no-op if no mounted table holds that row.
        ftCatalogRemoveRow: typeof requestCatalogRowRemoval;
        // Generic static card-filter (account hub etc.) — wires [data-ft-card-filter] search inputs
        // to show/hide [data-ft-card-filter-item] cards by name. No fetch; see card-filter.ts.
        ftScanCardFilters: typeof scanCardFilters;
        // The GM's campaign player table rendered into a host element (FT3D Workspace) — turn-widget-gm-controls.ts
        // "View Campaign Players". Returns false for a non-UUID campaign id.
        ftRenderCampaignPlayersTool: typeof renderCampaignPlayersTool;
        slideToggleEl?: (id: string) => void;
        // Footer rune scanner (template_page_footer.php) — hydrates .ft-rune-mount blobs into SVGs.
        // render-table.ts re-invokes it on the freshly-built table so async-injected name-cell runes
        // (spells/curses/mentas/sanguines) hydrate, mirroring the list-display/object-card bundles.
        ftMountRuneIcons?: (root?: Element | Document) => void;
        // Icon scanner (icon-bundle.js) — hydrates <i class="ft-icon"> placeholders (icon_ph) into
        // iconMap() spans. The catalog-list renderers inject endpoint HTML that may carry these
        // placeholders, so they re-invoke it scoped to the freshly-built container (via initTooltips).
        ftMountIcons?: (root?: Element | Document) => void;
        ftMountEncounterDisplays?: (root?: ParentNode) => void;
    }
}

Object.assign(window, { ftMountCatalogLists: mountCatalogLists, ftSortableScan: scanSortable, ftScanCardFilters: scanCardFilters, ftCatalogRemoveRow: requestCatalogRowRemoval, ftRenderCampaignPlayersTool: renderCampaignPlayersTool });

function ftInitTables(): void {
    mountCatalogLists();
    scanSortable();
    scanCardFilters();
}

// Mount immediately (the bundle is a footer <script>, so every mount above it is already parsed),
// then keep a DOMContentLoaded fallback for anything parsed after this point.
ftInitTables();
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ftInitTables);
}
